import {
    buildPageViewAttributes,
    getRoktConfig,
    getRoktConsent,
    initRokt,
    logPageView,
    setRoktJurisdiction,
} from '@eventbrite/rokt-sdk';
import { Stage } from '@eventbrite/webapp-constants';
import { useEffect, useState } from 'react';

let sdkInited = false;

export function resetRoktInitForTests() {
    if (process.env.NODE_ENV === 'test') {
        sdkInited = false;
    }
}

/**
 * Resolved client-side rather than from SSR props: the homepage response is
 * shared-cached at the CDN on a key that excludes the viewer, so a per-viewer
 * jurisdiction baked into the HTML would leak across viewers.
 */
function useRoktConfig() {
    const [config, setConfig] = useState<{
        apiKey?: string;
        jurisdiction?: 'EU' | 'ROW';
    } | null>(null);

    useEffect(() => {
        let cancelled = false;

        void (async () => {
            try {
                const response = await fetch('/home/api/rokt');
                const data: unknown = response.ok ? await response.json() : null;
                if (cancelled) {
                    return;
                }

                // A non-OK status or an unexpected payload leaves the
                // jurisdiction unestablished. The SDK reads an unset global as
                // ROW, so fail closed to EU rather than leaving it unset, and
                // drop the key so the SDK never loads on an untrusted payload
                // -- matching `parseRoktConfig` on the server.
                const body =
                    typeof data === 'object' && data !== null
                        ? (data as Record<string, unknown>)
                        : null;
                const jurisdiction =
                    body?.jurisdiction === 'EU' || body?.jurisdiction === 'ROW'
                        ? body.jurisdiction
                        : undefined;

                setConfig(
                    jurisdiction
                        ? {
                              apiKey: typeof body?.apiKey === 'string' ? body.apiKey : undefined,
                              jurisdiction,
                          }
                        : { apiKey: undefined, jurisdiction: 'EU' },
                );
            } catch {
                if (!cancelled) {
                    setConfig({ apiKey: undefined, jurisdiction: 'EU' });
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    return config;
}

export function RoktInit({ stage }: { stage: Stage }) {
    const config = useRoktConfig();
    const apiKey = config?.apiKey;
    const jurisdiction = config?.jurisdiction;

    useEffect(() => {
        if (typeof window !== 'undefined' && jurisdiction) {
            setRoktJurisdiction(jurisdiction);
        }

        if (!apiKey || sdkInited) {
            return;
        }

        // Deliberately not gated on `consent.block`: `initRokt` owns that
        // decision now and replays the init on Transcend's consent-change. This
        // effect only re-runs on stage/key/jurisdiction, and the homepage has no
        // reload-on-consent listener to fall back on, so returning early here
        // would leave a visitor who accepts the banner after mount without a
        // placement for the rest of the session.
        const consent = getRoktConsent();
        const isProd = stage === Stage.PROD;
        sdkInited = true;
        try {
            initRokt(getRoktConfig(isProd, apiKey), {
                noFunctional: consent.noFunctional,
                noTargeting: consent.noTargeting,
                // Logged from onLoad rather than inline so it also fires when a
                // blocked init is replayed: called inline, the emitter's gate
                // would drop it and nothing would retry it.
                onLoad: () =>
                    logPageView(
                        'home',
                        buildPageViewAttributes({
                            pageName: 'home',
                            path:
                                typeof window !== 'undefined'
                                    ? window.location.pathname
                                    : undefined,
                        }),
                    ),
            });
        } catch (error) {
            console.error('Failed to initialize Rokt SDK:', error);
            sdkInited = false;
        }
    }, [stage, apiKey, jurisdiction]);

    return null;
}
